import { useEffect, useState } from 'react';
import {
  useDateFormatPreference,
  useDispatch,
  useInvestment,
  useWeekStartsOnPreference,
} from '@/hooks/useStore';
import { addInvestment as dispatchAddInvestment } from '@/store/slices/investmentsSlice';
import DateTimeInput from '@/components/DateTimeInput';
import NumberInput from '@/components/NumberInput';
import PriceInput from '@/components/PriceInput';
import Button from '@/components/Button';
import { useToggle } from '@/hooks/useToggle';
import { formatNumber, formatPrice, isBlank } from '@/utils/strings';
import { useAppState } from '@/hooks/useAppState';
import { showSuccessToaster } from '@/utils/toasters';
import OptionsMenu from '@/components/OptionsMenu';
import IconButton from '@/components/IconButton';
import { CheckIcon, CloseIcon } from '@/icons';
import DeleteInvestmentDialog from '@/dialogs/DeleteInvestmentDialog';
import { parseSharePrice } from '@/utils/reports';
import { classes, createInvestment, updateInvestment } from './utils';

export type InvestmentFormProps = {
  companyId: number;
  investorId: number;
  investmentId?: number;
};

export default function InvestmentForm({
  companyId,
  investorId,
  investmentId,
}: InvestmentFormProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const weekStartsOn = useWeekStartsOnPreference();
  const dateFormat = useDateFormatPreference();
  const investment = useInvestment(investmentId);
  const [date, setDate] = useState(new Date());
  const [sharePrice, setSharePrice] = useState('');
  const [shareCount, setShareCount] = useState('');
  const [isEditing, toggleEditing] = useToggle(!investmentId);
  const [isDeleteDialogOpen, toggleDeleteDialog] = useToggle(false);
  const [isSaving, toggleSaving] = useToggle(false);

  useEffect(() => {
    if (investment) {
      setDate(new Date(investment.date));
      setSharePrice(formatPrice(parseSharePrice(investment)));
      setShareCount(formatNumber(investment.shareCount));
    }
  }, [investment]);

  const handleSave = async () => {
    try {
      toggleSaving(true);
      if (investmentId) {
        const updatedInvestment = await updateInvestment({
          investmentId,
          date,
          sharePrice,
          shareCount,
        });
        dispatch(dispatchAddInvestment(updatedInvestment));
        showSuccessToaster('Investment updated');
        toggleEditing(false);
      } else {
        const newInvestment = await createInvestment({
          companyId,
          investorId,
          date,
          sharePrice,
          shareCount,
        });
        dispatch(dispatchAddInvestment(newInvestment));
        showSuccessToaster('Investment added');
      }
      toggleSaving(false);
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving investment');
    }
  };

  const handleCancel = () => {
    // Revert to original values
    setDate(new Date(investment!.date));
    setSharePrice(formatPrice(parseSharePrice(investment!)));
    setShareCount(formatNumber(investment!.shareCount));
    toggleEditing(false);
  };

  const renderOptions = () => {
    const disabled = isSaving || isBlank(sharePrice) || isBlank(shareCount);
    if (!investmentId) {
      return (
        <Button
          disabled={disabled}
          className={classes.addButton}
          onClick={handleSave}>
          Add
        </Button>
      );
    }
    if (isEditing) {
      return (
        <div className={classes.iconsWrapper}>
          <IconButton
            disabled={isSaving}
            className={classes.closeIcon}
            icon={<CloseIcon />}
            onClick={handleCancel}
          />
          <IconButton
            disabled={disabled}
            icon={<CheckIcon />}
            onClick={handleSave}
          />
        </div>
      );
    } else {
      return (
        <OptionsMenu
          onEdit={() => toggleEditing(true)}
          onDelete={() => toggleDeleteDialog(true)}
        />
      );
    }
  };

  return (
    <>
      {isDeleteDialogOpen && (
        <DeleteInvestmentDialog
          investmentId={investmentId!}
          onClose={toggleDeleteDialog}
        />
      )}
      <form className={classes.wrapper}>
        <div className={classes.fieldsWrapper}>
          <DateTimeInput
            disabled={isSaving || !isEditing}
            disablePast={false}
            showTime={false}
            className={classes.dateTime}
            dateFormat={dateFormat}
            weekStartsOn={weekStartsOn}
            name="date"
            label="Date"
            value={date}
            onChange={(update) => setDate(update.date)}
          />
          <NumberInput
            disabled={isSaving || !isEditing}
            className={classes.shareCount}
            value={shareCount}
            name="shareCount"
            label="Shares"
            onChange={(update) => setShareCount(update.shareCount)}
          />
          <PriceInput
            disabled={isSaving || !isEditing}
            className={classes.priceInput}
            size="small"
            name="sharePrice"
            label="Share Price"
            value={sharePrice}
            onChange={setSharePrice}
          />
        </div>
        <div className={classes.optionsWrapper}>{renderOptions()}</div>
      </form>
    </>
  );
}
